/**
 * PublicHeader · Wave Codex-R8 · 2026-05-19
 *
 * Single header for public/unauthenticated pages (login, register, forgot
 * password, reset password, public token error states). Replaces the
 * `absolute right-4 top-4` LanguageToggle + ThemeToggle pair that was
 * colliding with the hero content on 390x844 mobile viewports.
 *
 * Mobile (<= sm):
 *   - Single 44x44 "preferências" cog button on the right.
 *   - Tap opens an inline panel directly under the header with the full
 *     LanguageToggle (segmented) and ThemeToggle (inline row), giving the
 *     user the same controls without two competing pill controls floating
 *     over the page hero.
 *
 * Desktop (>= sm):
 *   - LanguageToggle (pill) + ThemeToggle (icon) inline on the right, as
 *     before — there is no collision risk above sm because the form card
 *     is centered with comfortable margin.
 *
 * The header is sticky at z-30 with a min-height of 56px so the page
 * content below can keep its existing top padding/centering and the
 * header never overlaps the H1.
 */
import { useEffect, useRef, useState } from 'react';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { brandBaseUrl } from '@/lib/public-domain'; // W157 · 2026-06-01

interface PublicHeaderProps {
  /** Optional className for the outer header element. */
  className?: string;
  /**
   * When true, the header sits on top of an aurora/gradient backdrop and
   * uses a transparent background. Otherwise it gets a soft surface tint
   * so the toggles remain readable.
   */
  transparent?: boolean;
}

function CogIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.34.22.7.22 1.08V11a2 2 0 1 1 0 4h-.09c-.39 0-.74.08-1.08.22z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

export function PublicHeader({ className, transparent = false }: PublicHeaderProps) {
  const { t } = useT();
  const common = t.publicErrors.common;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Click-outside to dismiss the mobile panel.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <header
      className={cn(
        // z-30 so the header sits above the form card (z-10) but below
        // future Toast/CommandBar overlays (z-50). min-h 56px guarantees
        // the H1 below never reads as overlapped.
        'sticky top-0 z-30 w-full',
        transparent
          ? 'bg-transparent'
          : 'border-b border-line/60 bg-surface/80 backdrop-blur',
        className,
      )}
      data-testid="public-header"
    >
      <div className="mx-auto flex min-h-[56px] w-full max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
        {/* Logo / wordmark */}
        <a
          href={brandBaseUrl()}
          className="pressable focus-ring inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-ink"
          aria-label="Builder"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className="text-brand-500"
            aria-hidden="true"
          >
            <path
              d="M3 10.5L12 3l9 7.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 10.5V20a1 1 0 0 0 1 1h4v-5h4v5h4a1 1 0 0 0 1-1V10.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[14px] font-semibold tracking-tight">Builder</span>
        </a>

        {/* Desktop controls — inline. Hidden on mobile to avoid the two
            pill controls colliding with the hero. */}
        <div
          className="hidden items-center gap-2 sm:flex"
          data-testid="public-header-desktop-controls"
        >
          <LanguageToggle />
          <ThemeToggle />
        </div>

        {/* Mobile control — single cog button that opens an inline panel. */}
        <div className="sm:hidden">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls="public-header-prefs-panel"
            aria-label={common.settingsButtonLabel}
            className="pressable focus-ring inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface-2 text-ink hover:bg-surface-3"
            data-testid="public-header-prefs-button"
          >
            <CogIcon />
          </button>
        </div>
      </div>

      {/* Mobile preferences panel — anchored under the header, full-width
          card. Inline (not modal) so it never traps focus, but click-outside
          and Esc close it. */}
      {open && (
        <div
          ref={panelRef}
          id="public-header-prefs-panel"
          role="dialog"
          aria-label={common.settingsPanelTitle}
          className="border-t border-line bg-surface px-4 py-3 sm:hidden"
          data-testid="public-header-prefs-panel"
        >
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
              {common.settingsPanelTitle}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={common.settingsCloseLabel}
              className="pressable focus-ring inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-2 hover:text-ink"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="mt-3 space-y-3">
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-soft">
                {common.settingsLanguageLabel}
              </p>
              <LanguageToggle variant="inline" />
            </div>
            <div>
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-soft">
                {common.settingsThemeLabel}
              </p>
              <ThemeToggle variant="inline" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default PublicHeader;
